// Verfügbarkeits-Dialog (Slot blockieren / öffnen) —
// extrahiert aus AppointmentsPage.js.
// 06/2026 erweitert: studioweite Sperre (ganzes Studio) + Ganztags-Option.
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// 06/2026: Endzeit-Optionen in 15-Min-Schritten ab dem gewählten Slot-Start,
// damit exakte Kurz-Sperren (z.B. 17:45–18:00) möglich sind (vorher nur volle
// Stunden → man konnte einzelne 15-Min-Slots nicht präzise sperren).
const addMinutes = (hhmm, mins) => {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  let total = h * 60 + m + mins;
  if (total > 23 * 60 + 59) total = 23 * 60 + 59;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const endTimeOptions = (start) => {
  const out = [];
  const seen = new Set();
  for (const step of [15, 30, 45, 60, 90, 120]) {
    const t = addMinutes(start, step);
    if (t > (start || '') && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
};

export const AvailabilityDialog = ({
  open,
  onOpenChange,
  slot,
  onAddBlock,
  onAddException,
  onRemoveBlock,
  onRemoveBlockException,
  onAddBlockAllDay,
  onAddStudioBlock,
  onRemoveStudioBlock,
}) => {
  const [reason, setReason] = React.useState('');
  const reasonPresets = ['Wartung', 'Ferien', 'Krankheit', 'Sitzung'];
  React.useEffect(() => { if (!open) setReason(''); }, [open]);
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>
          {slot?.studioBlockedBy
            ? '🏢 Studio-Sperre'
            : slot?.isAvailable
            ? '⏸️ Slot sperren'
            : '✅ Slot öffnen'}
        </DialogTitle>
      </DialogHeader>

      {slot && (
        <div className="space-y-4">
          {/* Info */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium">{slot.trainerName}</p>
            <p className="text-sm text-gray-600">
              {new Date(slot.date).toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
            <p className="text-lg font-bold text-teal-600">{slot.timeSlot} Uhr</p>
          </div>

          {slot.studioBlockedBy ? (
            // Slot ist durch eine STUDIOWEITE Sperre gesperrt → aufheben anbieten
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="studio-blocked-info">
                <p className="text-sm text-red-800">
                  🏢 Dieses Studio ist gesperrt
                  {slot.studioBlockedBy.all_day
                    ? ' (ganztägig)'
                    : ` (${slot.studioBlockedBy.start_time}–${slot.studioBlockedBy.end_time} Uhr)`}
                  {slot.studioBlockedBy.reason ? ` — ${slot.studioBlockedBy.reason}` : ''}.
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Betrifft ALLE Trainer &amp; Buchungen in diesem Studio.
                </p>
              </div>
              <Button
                onClick={onRemoveStudioBlock}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="remove-studio-block-btn"
              >
                🗑️ Studio-Sperre aufheben
              </Button>
            </div>
          ) : slot.isAvailable ? (
            // Slot ist frei → sperren anbieten (Trainer ODER ganzes Studio)
            <div className="space-y-4">
              {slot.exceptionOpen && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-2" data-testid="exception-open-info">
                  <p className="text-sm text-amber-800">
                    ⚠️ Dieser Bereich liegt in einem Blocker
                    ({slot.exceptionOpen.block?.start_time}–{slot.exceptionOpen.block?.end_time} Uhr),
                    der per Ausnahme wieder geöffnet wurde.
                  </p>
                  <Button
                    variant="outline"
                    onClick={onRemoveBlockException}
                    className="w-full border-amber-400 text-amber-800 hover:bg-amber-100"
                    data-testid="remove-block-exception-btn"
                  >
                    🗑️ Blocker &amp; Ausnahme entfernen
                  </Button>
                </div>
              )}

              {/* --- Optionaler Sperr-Grund --- */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">📝 Grund (optional)</p>
                <div className="flex flex-wrap gap-1.5">
                  {reasonPresets.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setReason((prev) => (prev === r ? '' : r))}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${reason === r ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                      data-testid={`block-reason-preset-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="z.B. Wartung, Ferien, privat …"
                  className="text-sm"
                  data-testid="block-reason-input"
                />
              </div>

              {/* --- Trainer sperren --- */}
              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">👤 Trainer sperren</p>
                <p className="text-xs text-gray-500">Endzeit für die Blockierung:</p>
                <div className="grid grid-cols-3 gap-2">
                  {endTimeOptions(slot.timeSlot).map((endTime) => (
                    <Button
                      key={endTime}
                      variant="outline"
                      size="sm"
                      onClick={() => onAddBlock(endTime, reason)}
                      className="hover:bg-red-50 hover:border-red-300"
                      data-testid={`block-trainer-until-${endTime}`}
                    >
                      Bis {endTime}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={() => onAddBlockAllDay(reason)}
                  className="w-full hover:bg-red-50 hover:border-red-300"
                  data-testid="block-trainer-allday"
                >
                  🚫 Trainer ganztägig sperren
                </Button>
              </div>

              {/* --- Ganzes Studio sperren --- */}
              <div className="space-y-2 border-t pt-3">
                <p className="text-sm font-semibold text-gray-700">🏢 Ganzes Studio sperren</p>
                <p className="text-xs text-gray-500">
                  Blockiert ALLE Trainer &amp; Buchungen im Studio in diesem Zeitraum.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {endTimeOptions(slot.timeSlot).map((endTime) => (
                    <Button
                      key={endTime}
                      variant="outline"
                      size="sm"
                      onClick={() => onAddStudioBlock(endTime, false, reason)}
                      className="hover:bg-orange-50 hover:border-orange-300"
                      data-testid={`block-studio-until-${endTime}`}
                    >
                      Bis {endTime}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={() => onAddStudioBlock(null, true, reason)}
                  className="w-full hover:bg-orange-50 hover:border-orange-300"
                  data-testid="block-studio-allday"
                >
                  🏢 Studio ganztägig sperren
                </Button>
              </div>
            </div>
          ) : (
            // Slot ist nicht verfügbar
            slot.blockedByBlock ? (
              // Slot ist durch einen MANUELLEN Trainer-Block gesperrt → aufheben
              <div className="space-y-3">
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="blocked-by-block-info">
                  <p className="text-sm text-red-800">
                    ⏸️ Dieser Slot ist manuell blockiert
                    {slot.blockedByBlock.all_day
                      ? ' (ganztägig)'
                      : ` (${slot.blockedByBlock.start_time}–${slot.blockedByBlock.end_time} Uhr)`}
                    {slot.blockedByBlock.reason ? ` — ${slot.blockedByBlock.reason}` : ''}.
                  </p>
                </div>
                <Button
                  onClick={onRemoveBlock}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  data-testid="remove-block-btn"
                >
                  🗑️ Blockierung aufheben
                </Button>
              </div>
            ) : (
              // Slot außerhalb der regulären Verfügbarkeit → per Ausnahme öffnen
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Dieser Slot ist ausserhalb der regulären Verfügbarkeit.
                  Möchten Sie ihn trotzdem öffnen?
                </p>
                <Button
                  onClick={onAddException}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  data-testid="open-slot-exception-btn"
                >
                  ✅ Ja, Slot öffnen (30 Min)
                </Button>
                <p className="text-xs text-gray-500">
                  Der Slot wird für Buchungen freigegeben (nur für diesen Tag).
                </p>
              </div>
            )
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
  );
};
